import type { TailoredCVOverrides } from '@tabbio/types';
import type { CvWriteKind } from './cv-write-coordinator';

/**
 * B02: which revision each editor write states, and what the conflict prompt
 * explains when it is stale.
 *
 * Base:
 * - `draft`: the revision the sheet or edit page was opened with. Its form
 *   was filled once from that version, so a later refetch must not move it.
 * - `latest`: the latest revision this client has seen. For state the editor
 *   re-reads from the server on every refetch (style panel, section order).
 * - `none`: no `If-Match`; the server's bounded merge-retry applies (B01).
 *
 * Kind (prompt message), by what "apply my change on top" resends:
 * - `field`: only the fields the user edited (headline, summary, contact
 *   fields through the profile route, one project, the section order).
 *   Re-applying keeps every other change made elsewhere.
 * - `section`: a whole section (all roles, all skills, the header object).
 *   Re-applying replaces that section, so the prompt says so.
 * - `config`: the whole design (layout, colour, photo, share card).
 */
export type CvWriteBase = 'draft' | 'latest' | 'none';

export interface CvWritePolicy {
  kind: CvWriteKind;
  base: CvWriteBase;
  coalesceKey?: string;
}

/** i18n keys of the conflict prompt (seeker namespace). */
export const CV_CONFLICT_COPY_KEYS = {
  title: 'cv.conflict.title',
  message: {
    field: 'cv.conflict.messageField',
    section: 'cv.conflict.messageSection',
    config: 'cv.conflict.messageConfig',
  },
  apply: 'cv.conflict.apply',
  discard: 'cv.conflict.discard',
  keep: 'cv.conflict.keepEditing',
} as const;

type OverridesPatch = Partial<Omit<TailoredCVOverrides, 'version'>>;

/** Overrides fields whose patch is the user's own value, not a whole section. */
const OVERRIDE_FIELD_KEYS = new Set(['headline']);

/** PATCH /api/cv/:id/overrides from the tailored editor. */
export function describeOverridesWrite(patch: OverridesPatch): CvWritePolicy {
  const keys = Object.keys(patch ?? {});
  if (keys.length === 1 && keys[0] === 'cvConfig') {
    // Web tailored layout: the debounced style panel.
    return { kind: 'config', base: 'latest', coalesceKey: 'overrides:cvConfig' };
  }
  if (keys.length === 1 && keys[0] === 'sectionsConfig') {
    // Section show/hide/reorder from the layout.
    return { kind: 'field', base: 'latest', coalesceKey: 'overrides:sectionsConfig' };
  }
  if (keys.length > 0 && keys.every((key) => key === 'cvConfig' || key === 'sectionsConfig')) {
    return { kind: 'config', base: 'latest' };
  }
  if (keys.length > 0 && keys.every((key) => OVERRIDE_FIELD_KEYS.has(key))) {
    return { kind: 'field', base: 'draft' };
  }
  return { kind: 'section', base: 'draft' };
}

/** PATCH /api/cv/:id. */
export function describeCvUpdateWrite(patch: object): CvWritePolicy {
  const keys = Object.keys(patch ?? {});
  if (keys.length > 0 && keys.every((key) => key === 'title')) {
    // A rename from the CV list: the user typed the whole value and the
    // server merges a title-only patch safely, so no prompt is needed.
    return { kind: 'field', base: 'none' };
  }
  if (keys.includes('sections')) return { kind: 'section', base: 'draft' };
  return { kind: 'field', base: 'draft' };
}

/** `/api/cv/:target/profile/*` writes on a CV target (main editor, shared CVs). */
export const PROFILE_WRITE_POLICY = {
  /** PATCH /profile: header and summary fields; the server merges them. */
  profile: { kind: 'field', base: 'draft' },
  /** PUT/DELETE experience, education, certification, highlight: the main editor resends every item. */
  item: { kind: 'section', base: 'draft' },
  /** PUT/DELETE project: the projects editor saves one project at a time. */
  project: { kind: 'field', base: 'draft' },
  /** PUT skills / languages: the whole list. */
  list: { kind: 'section', base: 'draft' },
  /** PUT cv-config: the whole design, from the style panel (debounced). */
  cvConfig: { kind: 'config', base: 'latest', coalesceKey: 'cv-config' },
  /** PUT sections-config: the section order. */
  sectionsConfig: { kind: 'field', base: 'latest', coalesceKey: 'sections-config' },
} as const satisfies Record<string, CvWritePolicy>;
